import React from "react";
import { useNavigate } from "react-router-dom";
import { Workflow, PlayCircle, Zap } from "lucide-react";
import { LanguageCode } from "../../utils/translations";
import { HOME_COPY } from "./homeContent";
import { HOME_THEME, primaryBtn, secondaryBtn } from "./theme";
import HeroShowcase from "./HeroShowcase";

interface HeroSectionProps {
  lang: LanguageCode;
}

const HeroSection: React.FC<HeroSectionProps> = ({ lang }) => {
  const navigate = useNavigate();
  const c = HOME_COPY[lang].hero;

  return (
    <section
      style={{
        padding: "36px 5% 40px",
        background: `linear-gradient(160deg, ${HOME_THEME.surfaceAlt} 0%, ${HOME_THEME.surface} 60%)`,
      }}
    >
      <div
        className="home-hero__content"
        style={{
          maxWidth: "1280px",
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: "1.05fr 0.95fr",
          gap: "48px",
          alignItems: "center",
        }}
      >
        <div>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              background: HOME_THEME.primarySoft,
              color: HOME_THEME.primaryDark,
              padding: "6px 14px",
              borderRadius: "999px",
              fontSize: "0.8rem",
              fontWeight: 700,
              marginBottom: "18px",
            }}
          >
            <Workflow size={14} /> {c.badge}
          </span>

          <h1
            style={{
              fontSize: "2.9rem",
              fontWeight: 900,
              lineHeight: 1.1,
              color: HOME_THEME.heroHeading,
              margin: "0 0 1rem 0",
              letterSpacing: "-0.5px",
            }}
          >
            {c.headingPre}
            <span style={{ color: HOME_THEME.primary }}>
              {c.headingHighlight}
            </span>
          </h1>

          <p
            style={{
              fontSize: "1.1rem",
              color: HOME_THEME.muted,
              lineHeight: 1.65,
              margin: "0 0 1.2rem 0",
              maxWidth: "580px",
            }}
          >
            {c.intro}
          </p>

          <p
            style={{
              fontSize: "0.88rem",
              color: HOME_THEME.inkSoft,
              lineHeight: 1.6,
              margin: "0 0 1.4rem 0",
              maxWidth: "620px",
            }}
          >
            <strong style={{ color: HOME_THEME.primaryDark }}>
              {c.alsoLabel}
            </strong>{" "}
            {c.alsoList}
          </p>

          <div style={{ display: "flex", gap: "14px", flexWrap: "wrap" }}>
            <button style={primaryBtn} onClick={() => navigate("/automate-business")}>
              {c.primaryBtn}
            </button>
            <button
              style={{
                ...secondaryBtn(),
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
              }}
              onClick={() => navigate("/social")}
            >
              <PlayCircle size={18} /> {c.secondaryBtn}
            </button>
          </div>
        </div>

        {/* Auto-scrolling showcase of every capability & service */}
        <HeroShowcase lang={lang} />
      </div>

      {/* Tech badge strip */}
      <div
        style={{
          maxWidth: "1280px",
          margin: "24px auto 0",
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: "8px",
        }}
      >
        {c.badges.map((b) => (
          <span
            key={`strip-${b}`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              color: HOME_THEME.inkSoft,
              background: HOME_THEME.surface,
              border: `1px solid ${HOME_THEME.border}`,
              borderRadius: "999px",
              padding: "6px 14px",
              fontSize: "0.8rem",
              fontWeight: 600,
            }}
          >
            <Zap size={12} color={HOME_THEME.primary} /> {b}
          </span>
        ))}
      </div>
    </section>
  );
};

export default HeroSection;
